import NumberHelper from "@/module/NumberHelper";
import FormHelper from "@/module/FormHelper";
import FormItem from "@/module/FormItem";
import ExchangeRateProvider from "@/module/ExchangeRate/ExchangeRateProvider";
import { ItemType } from "@/module/Item/ItemType";
import PaymentMethodItemResource from "@/module/Form/Resources/PaymentMethodItemResource";
import FormItemPrice from "@/module/FormItemPrice";

export default class FormPaymentMethodItem {
  readonly paymentMethodResource: PaymentMethodItemResource;
  prices: { [key: string]: number } = {};
  formItems: FormItem[] = [];

  constructor(paymentMethodResource: PaymentMethodItemResource) {
    this.paymentMethodResource = paymentMethodResource;
  }

  async updatePrices(
    currency: string,
    domesticCurrency: string,
    exchangeRateProvider: ExchangeRateProvider,
    vatRate: number,
  ) {
    const prices = await this.process(
      currency,
      domesticCurrency,
      exchangeRateProvider,
    );
    const formItems = await this.createItems(
      prices,
      currency,
      vatRate,
      exchangeRateProvider,
    );

    this.prices = prices;
    this.formItems = formItems;
  }

  findFormItemByCountryCode(countryCode: string | null): FormItem | null {
    const item = this.formItems.find((item: FormItem) => {
      return item.name === countryCode;
    });

    if (item === undefined) {
      return null;
    }

    return item;
  }

  getCountriesOptions(): Array<string> {
    const out = [];

    for (const item of this.formItems) {
      out.push(item.name);
    }

    return out;
  }

  private async process(
    currency: string,
    domesticCurrency: string,
    exchangeRateProvider: ExchangeRateProvider,
  ): Promise<{ [key: string]: number }> {
    const prices = {};

    for (const addressPrice of this.paymentMethodResource.address_prices) {
      const price = this.findAddressPrice(addressPrice.prices, currency);

      if (price !== undefined) {
        FormPaymentMethodItem.addPrice(
          prices,
          FormPaymentMethodItem.getKeyByType(addressPrice),
          price.price,
        );

        continue;
      }

      const domesticAddressPrice = this.findAddressPrice(
        addressPrice.prices,
        domesticCurrency,
      );

      let domesticPrice = 0;

      if (domesticAddressPrice) {
        domesticPrice = domesticAddressPrice.price;
      } else if (addressPrice.prices !== undefined) {
        let firstPriceObject: {
          currency_code: string;
          price: number;
        } | null = null;

        for (const tmpPrice of addressPrice.prices) {
          firstPriceObject = tmpPrice;
          break;
        }

        if (firstPriceObject === null) {
          throw new Error("Invalid payment method item data.");
        }

        domesticPrice = (await FormHelper.toDomesticCurrency(
          firstPriceObject.price,
          firstPriceObject.currency_code,
          exchangeRateProvider,
          true,
        )) as number;
      }

      if (currency === domesticCurrency) {
        FormPaymentMethodItem.addPrice(
          prices,
          FormPaymentMethodItem.getKeyByType(addressPrice),
          domesticPrice,
        );

        continue;
      }

      FormPaymentMethodItem.addPrice(
        prices,
        FormPaymentMethodItem.getKeyByType(addressPrice),
        FormHelper.toSourceCurrency(
          domesticPrice,
          currency,
          exchangeRateProvider,
        ) as number,
      );
    }

    return prices;
  }

  private static addPrice(prices: any, key: string, price: number): void {
    prices[key] = NumberHelper.round(price);
  }

  private findAddressPrice(
    addressPrices: { currency_code: string; price: number }[] | undefined,
    currency: string,
  ) {
    if (addressPrices === undefined) {
      return undefined;
    }

    return addressPrices.find((price) => {
      return price.currency_code === currency;
    });
  }

  private async createItems(
    prices: { [key: string]: number },
    currency: string,
    vatRate: number,
    exchangeRateProvider: ExchangeRateProvider,
  ): Promise<Array<FormItem>> {
    const formItems = [];

    for (const name in prices) {
      const price = prices[name];

      const formItem = new FormItem(
        0,
        0,
        [
          new FormItemPrice({
            type: "one_time",
            price: price,
            currency_code: currency,
            metadata: null,
            repayment_count: null,
          }),
        ],
        this.paymentMethodResource.including_vat,
        0,
        name,
        null,
        null,
        false,
        true,
        ItemType.PAYMENT_METHOD,
        false,
        false,
        false,
        null,
        false,
        false,
        null,
        null,
        { weight: 0, live_event_country: null, is_disable_repayment: false },
      );

      formItems.push(formItem);
      await formItem.recalculate(
        vatRate,
        currency,
        currency,
        exchangeRateProvider,
        false,
        false,
      );
    }

    return formItems;
  }

  private static getKeyByType(addressPrice: {
    address: { simple_address?: string; country_code?: string };
  }): string {
    return addressPrice.address.country_code as string;
  }
}
